import React from 'react';
import { Field } from 'redux-form';
import { Form } from '@syneto/compass-react';
import CreatableReactSelect from 'react-select/creatable';
import { flatAutoCompleteOptions } from '../../helpers/normalize-data';

/*
 * A single-value select whose value can also be typed in. Like creatableSelect.js, and unlike select.js, nothing is
 * mirrored in local state: the react-select value is derived from the redux-form value on every render, so a value
 * the option list does not carry (a cap such as 7.x.x saved before its releases exist) still shows and is kept.
 *
 * The field's redux-form value is the option's value: a string, or null when cleared or when the option is None.
 */

export const toSelectedOption = (value, options) => {
	if (value === null || value === undefined || value === '') {
		return flatAutoCompleteOptions(options ?? []).find((option) => { return option.value === null; }) ?? null;
	}

	return flatAutoCompleteOptions(options ?? []).find((option) => { return option.value === value; }) ?? { label: String(value), value };
};

const renderField = (props) => {
	const { options, disabled, input, label, emptyOption, required, meta, onValueChange, formatCreateLabel } = props;
	const { touched, error, warning } = meta;
	const { value, onChange } = input;
	const selectedOption = toSelectedOption(value, options);

	const handleChange = (selected) => {
		const next = selected ? selected.value : null;

		onChange(next);

		if (onValueChange) {
			onValueChange(next);
		}
	};

	const isValidNewOption = (inputValue) => {
		const candidate = String(inputValue ?? '').trim();

		return Boolean(candidate) && !flatAutoCompleteOptions(options ?? []).some((option) => { return option.value === candidate; });
	};

	return (
		<Form.Group className="mb-3">
			{ label ? <Form.Label>{ label }{ required ? '*' : null }</Form.Label> : null }

			<CreatableReactSelect
				unstyled
				className={`form-control ${ touched && error ? 'is-invalid' : ''}`}
				classNamePrefix='form-control'
				options={options}
				isDisabled={disabled}
				onChange={handleChange}
				placeholder={emptyOption}
				value={selectedOption}
				isValidNewOption={isValidNewOption}
				createOptionPosition="first"
				formatCreateLabel={formatCreateLabel ?? ((inputValue) => { return `Use "${inputValue}"`; })}
			/>

			{ touched && warning && <span>{ warning }</span> }

			<Form.Control.Feedback type="invalid">
				{ error }
			</Form.Control.Feedback>
		</Form.Group>
	);
};

const ComboSelect = (props) => { return <Field { ...props } component={ renderField }/>; };

export default ComboSelect;
